import debounce from 'lodash/debounce';
import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import { usePluginStateEffect } from '@atlaskit/editor-common/use-plugin-state-effect';
import type { CollabEditPlugin } from '@atlaskit/editor-plugin-collab-edit';

import { useNativeCollabState } from '@confluence/native-collab';

export type SaveIndicatorContextType = {
	isSaving: boolean;
	savedTime: Date;
};

export type TriggerSaveIndicatorContextType = {
	triggerSaveIndicator: () => void;
};

export const SaveIndicatorContext = createContext<SaveIndicatorContextType>({
	isSaving: false,
	savedTime: new Date(),
});

// Used to force the Save Indicator to display and then auto-resolve to saved based on timer, for changes outside document body
// So this is not reflective of the actual data being saved (whatever is using it, e.g. title update), really it should take in a promise or callback to indicate when the external save is successful/failed
export const TriggerSaveIndicatorContext = createContext<TriggerSaveIndicatorContextType>({
	triggerSaveIndicator: () => {},
});

const DEBOUNCE_SAVE_PERIOD = 1200;
const COMMIT_STATUS_DEBOUNCE = 500;
const NCS_POLLING_INTERVAL = 500;

type SaveIndicatorContextProviderProps = {
	editorApi?: PublicPluginAPI<[CollabEditPlugin]> | null;
	children: React.ReactNode;
};

export const SaveIndicatorContextProvider = ({
	editorApi,
	children,
}: SaveIndicatorContextProviderProps) => {
	const [isSaving, setIsSaving] = useState(false);
	const [savedTime, setSavedTime] = useState<Date>(new Date());
	const saveAttemptCallbackId = useRef<number>(0);
	const saveCompleteCallbackId = useRef<number>(0);
	const pollIntervalId = useRef<any>(0);
	const [{ collabEditProvider }] = useNativeCollabState();
	const previousBodyChangeTimeRef = useRef<number | null | undefined>(undefined);

	// These window functions are used here as performance efficient callback throttlers
	// requestIdleCallback is not supported by all browsers, hence fallback to requestAnimationFrame
	const throttledCallback = window.requestIdleCallback || window.requestAnimationFrame;

	const cancelThrottle = useCallback(
		(id: number) => {
			if (throttledCallback === window.requestIdleCallback) {
				window.cancelIdleCallback(id);
			} else {
				window.cancelAnimationFrame(id);
			}
		},
		[throttledCallback],
	);

	const onSaveSuccess = useCallback(() => {
		if (saveCompleteCallbackId.current) {
			cancelThrottle(saveCompleteCallbackId.current);
		}
		const id = throttledCallback(() => {
			setSavedTime(new Date());
			setIsSaving(false);
			clearInterval(pollIntervalId.current);
			pollIntervalId.current = 0;
			saveCompleteCallbackId.current = 0;
		});
		saveCompleteCallbackId.current = id;
	}, [cancelThrottle, throttledCallback]);

	const pollForStepsSaved = useCallback(() => {
		if (!collabEditProvider) {
			return;
		}

		const unconfirmedSteps = collabEditProvider.getUnconfirmedSteps();
		if (!unconfirmedSteps || unconfirmedSteps.length === 0) {
			onSaveSuccess();
		}
	}, [collabEditProvider, onSaveSuccess]);

	const onSaveAttempt = useCallback(() => {
		if (saveAttemptCallbackId.current) {
			cancelThrottle(saveAttemptCallbackId.current);
		}
		if (pollIntervalId.current) {
			clearInterval(pollIntervalId.current);
		}

		const attemptId = throttledCallback(() => {
			setIsSaving(true);
			saveAttemptCallbackId.current = 0;

			const id = setInterval(pollForStepsSaved, NCS_POLLING_INTERVAL);
			pollIntervalId.current = id;
		});
		saveAttemptCallbackId.current = attemptId;
	}, [throttledCallback, cancelThrottle, pollForStepsSaved]);

	// Update polling interval callback with new poll() whenever it updates
	// Because setInterval callback does not track state changes (i.e. isSaving)
	useEffect(() => {
		if (pollIntervalId.current) {
			clearInterval(pollIntervalId.current);
			pollIntervalId.current = setInterval(pollForStepsSaved, NCS_POLLING_INTERVAL);
		}
	}, [pollForStepsSaved]);

	const debouncedOnChange = useMemo(
		() => debounce(onSaveAttempt, COMMIT_STATUS_DEBOUNCE, { leading: true, trailing: false }),
		[onSaveAttempt],
	);

	usePluginStateEffect(editorApi, ['collabEdit'], ({ collabEditState }) => {
		const lastChangeTime = collabEditState?.initialised?.lastLocalOrganicBodyChangeAt;

		if (!collabEditState?.initialised || !lastChangeTime) {
			return;
		}

		if (lastChangeTime !== previousBodyChangeTimeRef.current) {
			previousBodyChangeTimeRef.current = lastChangeTime;
			debouncedOnChange();
		}
	});

	// Cancel any awaiting callbacks on unmount
	useEffect(() => {
		return () => {
			if (pollIntervalId.current) {
				clearInterval(pollIntervalId.current);
			}
			if (saveAttemptCallbackId.current) {
				cancelThrottle(saveAttemptCallbackId.current);
			}
			if (saveCompleteCallbackId.current) {
				cancelThrottle(saveCompleteCallbackId.current);
			}
		};
		// eslint-disable-next-line
	}, []);

	// display save indicator with auto resolve to saved, for changes outside document body
	const triggerSaving = useCallback(() => {
		let autoSuccessTimeoutId: ReturnType<typeof setTimeout>;
		if (!isSaving) {
			if (saveAttemptCallbackId.current) {
				cancelThrottle(saveAttemptCallbackId.current);
			}
			const attemptId = throttledCallback(() => {
				setIsSaving(true);
				saveAttemptCallbackId.current = 0;
				autoSuccessTimeoutId = setTimeout(onSaveSuccess, DEBOUNCE_SAVE_PERIOD);
			});
			saveAttemptCallbackId.current = attemptId;
		}

		return () => {
			if (autoSuccessTimeoutId) {
				clearTimeout(autoSuccessTimeoutId);
			}
		};
	}, [isSaving, throttledCallback, cancelThrottle, onSaveSuccess]);

	const saveIndicatorContextValue = useMemo(
		() => ({
			isSaving,
			savedTime,
		}),
		[isSaving, savedTime],
	);

	const saveIndicatorTriggerContextValue = useMemo(
		() => ({
			triggerSaveIndicator: triggerSaving,
		}),
		[triggerSaving],
	);

	return (
		<SaveIndicatorContext.Provider value={saveIndicatorContextValue}>
			<TriggerSaveIndicatorContext.Provider value={saveIndicatorTriggerContextValue}>
				{children}
			</TriggerSaveIndicatorContext.Provider>
		</SaveIndicatorContext.Provider>
	);
};
